import React from 'react'
import {
  OP_LABELS,
  creatingOp,
  diffCardSummary,
  diffCardTreatment,
  isOverrideRow as rowIsOverride,
  rowActions,
} from './blockChangeHelpers'
import BlockEditForm from './BlockEditForm'

const ACTION_LABELS = {
  'block.update': 'Edit',
  'block.override': 'Override here',
  'block.deactivate': 'Deactivate',
  'block.activate': 'Activate',
}

const conditionValues = (conditions) =>
  conditions.map((condition) => ({
    field_name: condition.field_name,
    field_value: condition.field_value,
  }))

// The open editor is held by the page so the drawer's "Edit rule" button can open it too.
export default function BlockChangeActions({
  detail,
  changeRequestId,
  treatmentId,
  vocabulary,
  onRetryVocabulary = null,
  isBusy,
  onSubmit,
  draftChanges = [],
  treatmentNamesById = {},
  actionLabels = {},
  editingOp,
  onEditingOpChange: setEditingOp,
}) {
  const { actions, overrideTreatmentId } = rowActions({ detail })
  const { table, row } = detail
  // An override evaluates with its ancestor's conditions, so conditions written on it would be ignored.
  const isOverrideRow = rowIsOverride(row)
  // Overriding an override starts from the original, so the form sends every field in effect, not just edits.
  const compareRow =
    editingOp === 'block.override' && isOverrideRow ? null : row

  const submit = (op, after, onFieldErrors) =>
    onSubmit({ op, after, onFieldErrors }).then((saved) => {
      if (saved) setEditingOp(null)
      return saved
    })

  const saveEdit = (after, onFieldErrors) =>
    submit(editingOp, after, onFieldErrors)

  // Edits open a form; deactivate and activate have nothing to fill in, so they go straight to the draft.
  const choose = (op) => {
    if (op === 'block.update' || op === 'block.override') setEditingOp(op)
    else submit(op, {})
  }

  return (
    <div
      className="lox-block-change__actions"
      data-testid="block-change-actions"
    >
      <h4 className="lox-blocks-explorer__section-title">
        Change in this draft
      </h4>
      {draftChanges.length > 0 && (
        <div
          className="notification is-info is-light is-size-7 p-2 mb-2"
          data-testid="block-draft-changes"
        >
          <ul>
            {draftChanges.map((card) => {
              const details = [
                diffCardTreatment({ card, treatmentNamesById }),
                diffCardSummary({ card, actionLabels }),
              ].filter(Boolean)
              return (
                <li key={card.sequence}>
                  <span className="has-text-grey">{card.sequence}</span>{' '}
                  {OP_LABELS[card.op] ?? card.op}
                  {details.length > 0 && ` · ${details.join(' · ')}`}
                </li>
              )
            })}
          </ul>
        </div>
      )}
      <div className="buttons">
        {actions.map((op) => (
          <button
            key={op}
            type="button"
            className={`button is-small ${editingOp === op ? 'is-active' : ''}`}
            disabled={isBusy}
            onClick={() => choose(op)}
            data-testid={`block-action-${op}`}
          >
            {ACTION_LABELS[op] ?? OP_LABELS[op]}
          </button>
        ))}
      </div>
      {editingOp && (
        <BlockEditForm
          key={editingOp}
          op={editingOp}
          table={table}
          row={compareRow}
          currentConditions={conditionValues(detail.conditions)}
          initialValues={{
            ...row,
            conditions: conditionValues(detail.conditions),
            ...(editingOp === 'block.override'
              ? { treatment_id: overrideTreatmentId }
              : {}),
          }}
          excludeFields={isOverrideRow ? ['conditions'] : []}
          createdByOp={creatingOp({ cards: draftChanges, id: row.id })}
          vocabulary={vocabulary}
          onRetryVocabulary={onRetryVocabulary}
          draftKey={{
            changeRequestId,
            treatmentId:
              editingOp === 'block.override'
                ? overrideTreatmentId
                : treatmentId,
            table,
            id: row.id,
            op: editingOp,
          }}
          isBusy={isBusy}
          onSave={saveEdit}
          onCancel={() => setEditingOp(null)}
        />
      )}
    </div>
  )
}
